/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

export type PartnerIri = `/affiliate_partners/${number}` | `/insurer_partners/${number}`;

export const PARTNER_IRIS = {
  ADVOCARD: "/insurer_partners/1",
  ARAG: "/insurer_partners/2",
  AUXILIA: "/insurer_partners/19",
  DEURAG: "/insurer_partners/17",
  HUK_COBURG: "/insurer_partners/12",
  LVM: "/insurer_partners/18",
  NRV: "/insurer_partners/15",
  NURNBERGER: "/insurer_partners/14",
  VHV: "/insurer_partners/13",
  WBS: "/affiliate_partners/7",
} as const;

const PARTNER_IRI_BY_NAME: Record<string, PartnerIri> = {
  advocard: PARTNER_IRIS.ADVOCARD,
  arag: PARTNER_IRIS.ARAG,
  auxilia: PARTNER_IRIS.AUXILIA,
  deurag: PARTNER_IRIS.DEURAG,
  hukCoburg: PARTNER_IRIS.HUK_COBURG,
  lvm: PARTNER_IRIS.LVM,
  nrv: PARTNER_IRIS.NRV,
  nurnberger: PARTNER_IRIS.NURNBERGER,
  vhv: PARTNER_IRIS.VHV,
  wbs: PARTNER_IRIS.WBS,
};

const PARTNER_NAME_BY_IRI: Record<PartnerIri, string> = Object.entries(PARTNER_IRI_BY_NAME).reduce(
  (partnerNames, [partnerName, partnerIri]) => {
    partnerNames[partnerIri] = partnerName;

    return partnerNames;
  },
  {} as Record<PartnerIri, string>,
);

export function isPartnerIri(value: unknown): value is PartnerIri {
  return typeof value === "string" && /^\/(affiliate|insurer)_partners\/\d+$/.test(value);
}

export function getPartnerIriByName(partnerName?: string | null): PartnerIri | null {
  if (!partnerName) {
    return null;
  }

  return PARTNER_IRI_BY_NAME[partnerName] ?? null;
}

export function getPartnerNameByIri(partnerIri?: string | null): string | null {
  if (!partnerIri || !isPartnerIri(partnerIri)) {
    return null;
  }

  return PARTNER_NAME_BY_IRI[partnerIri] ?? null;
}

export function getPartnerSlugByIri(partnerIri?: string | null): string | null {
  return getPartnerNameByIri(partnerIri);
}

export function isPartnerIriOneOf(partnerIri: string | null | undefined, partnerIris: PartnerIri[]): boolean {
  return !!partnerIri && partnerIris.includes(partnerIri as PartnerIri);
}

export function isInsurerPartnerIri(partnerIri?: string | null): boolean {
  return typeof partnerIri === "string" && partnerIri.startsWith("/insurer_partners/");
}
